import { useEffect } from 'react'

import {
  EscottMasterParameter,
  Maybe,
  PaymentMethod,
} from '@/clients/utoniq-core/schema'

/**
 * Escott のフォーム送信用関数
 *
 * 下記のようなフォームを作成し送信する
 * <form id="redirectForm" action="${nextActionUrl}" method="post">
 *   <input type="hidden" name="MerchantId" value="${param.merchantId}" />
 *   <input type="hidden" name="OperateId" value="${param.operateId}" />
 *   <input type="hidden" name="EncryptValue" value="${param.encryptValue}" />
 *   <input type="hidden" name="OpenID" value="${param.openId}" />
 * </form>
 *
 * 余計なパラメータを含ませてしまうと正しく動作しなくなるため注意
 *
 * Escott は仕様上 fetch や XMLHttpRequest 等では動作せず
 * form の POST メソッド限定で動作する
 *
 * @param actionUrl フォームの送信先URL
 * @param params フォームのパラメータ
 */
export const submitEscottForm = (
  actionUrl: string,
  params: EscottMasterParameter
) => {
  const form = document.createElement('form')

  form.method = 'POST'
  form.action = actionUrl

  // GraphQL のキー名 → e-scott が期待する name 属性名 のマッピング
  const NAME_MAP: Record<keyof EscottMasterParameter, string> = {
    merchantId: 'MerchantId',
    operateId: 'OperateId',
    encryptValue: 'EncryptValue',
    openID: 'OpenID',
  }

  Object.entries(params).forEach(([key, value]) => {
    // 値が null/undefined の場合はスキップ
    if (value === null || value === undefined) return

    // NAME_MAP に定義がないキーはスキップ
    if (!(key in NAME_MAP)) return

    const input = document.createElement('input')
    input.type = 'hidden'
    input.name = NAME_MAP[key as keyof EscottMasterParameter]
    input.value = value

    form.appendChild(input)
  })

  document.body.appendChild(form)
  form.submit()
}

/**
 * キャリア決済のメソッド
 */
export const CARRIER_PAYMENT_METHODS = [
  PaymentMethod.Au,
  PaymentMethod.Docomo,
  PaymentMethod.Softbank,
]
/**
 * キャリア決済のメソッドかどうかを判定する
 * @param paymentMethod 決済方法
 */
export const isCarrierPaymentMethod = (
  paymentMethod: Maybe<PaymentMethod> | undefined
) => {
  if (!paymentMethod) return false
  return CARRIER_PAYMENT_METHODS.includes(paymentMethod)
}

/**
 * ブラウザバックが BFCache で復元された際にコールバックを実行するフック
 * @param onRestore ページがBFCache から復元された時に実行するコールバック
 */
export const useBFCacheRestore = (onRestore: () => void) => {
  useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent) => {
      // BFCache 復元時
      if (event.persisted) {
        onRestore()
      }
    }
    window.addEventListener('pageshow', handlePageShow)
    return () => window.removeEventListener('pageshow', handlePageShow)
  }, [onRestore])
}
